import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const API = '';

export interface MasterUser {
  name: string;
  email: string;
}

interface MasterAuthContextType {
  user: MasterUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const MasterAuthContext = createContext<MasterAuthContextType>({
  user: null,
  token: null,
  loading: false,
  login: async () => {},
  logout: async () => {},
  isAuthenticated: false,
});

export const useMasterAuth = () => useContext(MasterAuthContext);

/** Chave usada no localStorage para persistir a sessão master */
const LS_TOKEN = 'master_token';
const LS_USER  = 'master_user';

export function MasterAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(LS_TOKEN));
  const [user, setUser]   = useState<MasterUser | null>(() => {
    const raw = localStorage.getItem(LS_USER);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/master/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || 'Credenciais inválidas');
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem(LS_TOKEN, data.token);
      localStorage.setItem(LS_USER, JSON.stringify(data.user));
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (token) {
      try {
        await fetch(`${API}/api/master/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignorar */ }
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
  }, [token]);

  return (
    <MasterAuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated: !!token }}>
      {children}
    </MasterAuthContext.Provider>
  );
}
